// src/oura/lib/spend.ts
//
// What a scan cost, written for a human (#3571).
//
// The one rule everything here follows: **`null` is not zero.** A run recorded
// before the backend started counting, and a run that genuinely cost nothing,
// are different facts. Rendering the first as "$0.00" invents a measurement,
// and it is the kind of invention nobody catches — the number looks fine.
//
// This mirrors the distinction the panel already keeps for scores, where `0` is
// a real score and `—` means not measured.

import type { AeoRunCost, AeoRunTokens } from "@service/aeo";

/** "$0.41". Cents, because a scan costs cents and rounding to dollars would
 *  render most runs as "$0". */
export function formatUsd(value: number): string {
    return `$${value.toFixed(2)}`;
}

/** "842", "205K", "1.2M". The exact figure belongs in a title, not here. */
export function formatTokens(value: number): string {
    if (value < 1_000) return String(value);
    if (value < 1_000_000) return `${Math.round(value / 1_000)}K`;
    return `${(value / 1_000_000).toFixed(1)}M`;
}

/** Exactly what a locale would print, for the tooltip. */
export function formatTokensExact(value: number): string {
    return value.toLocaleString("en-GB");
}

export interface SpendSummary {
    /** The money line, or null when we have nothing to say. */
    money: string | null;
    /** True when `money` is the flat estimate rather than recorded spend, so
     *  the caller can label it instead of passing it off as measured. */
    isEstimate: boolean;
    /** The token line, or null. */
    tokens: string | null;
    /** The full breakdown, for a tooltip. Null when there is nothing to break
     *  down. */
    detail: string | null;
}

/**
 * One run's spend, reduced to what the row should show.
 *
 * Returns all-null when the backend sent nothing — the caller renders nothing
 * at all in that case, rather than a dash or an empty column. An older backend
 * should leave the panel looking exactly as it did before this shipped.
 */
export function summariseSpend(
    cost: AeoRunCost | null | undefined,
    tokens: AeoRunTokens | null | undefined,
): SpendSummary {
    const actual = typeof cost?.actual_usd === "number" ? cost.actual_usd : null;
    const estimate = typeof cost?.estimated_usd === "number" ? cost.estimated_usd : null;
    const total = typeof tokens?.total === "number" ? tokens.total : null;

    // Recorded spend leads. The estimate is a flat per-scan figure that was
    // never computed for THIS run, so it only appears when there is nothing
    // real to show, and only ever with a label.
    const isEstimate = actual === null && estimate !== null;
    const money = actual !== null ? formatUsd(actual)
        : estimate !== null ? formatUsd(estimate)
            : null;

    const parts: string[] = [];
    if (actual !== null) parts.push(`Recorded spend ${formatUsd(actual)}`);
    if (estimate !== null) parts.push(`Flat estimate ${formatUsd(estimate)} per scan`);
    if (typeof tokens?.input === "number") parts.push(`${formatTokensExact(tokens.input)} input tokens`);
    if (typeof tokens?.output === "number") parts.push(`${formatTokensExact(tokens.output)} output tokens`);
    if (total !== null && typeof tokens?.input !== "number" && typeof tokens?.output !== "number") {
        parts.push(`${formatTokensExact(total)} tokens`);
    }

    return {
        money,
        isEstimate,
        tokens: total !== null ? `${formatTokens(total)} tokens` : null,
        detail: parts.length > 0 ? parts.join(" · ") : null,
    };
}

/**
 * The panel footer: recorded spend across the runs on screen.
 *
 * Reports how many rows it actually covers whenever some have no figure. A
 * total that silently skips them reads as the whole story, and the reader has
 * no way to tell it is not.
 */
export function summariseTotal(
    runs: { cost?: AeoRunCost | null }[],
): { text: string } | null {
    const withCost = runs.filter((r) => typeof r.cost?.actual_usd === "number");
    if (withCost.length === 0) return null;
    const total = withCost.reduce((sum, r) => sum + (r.cost?.actual_usd ?? 0), 0);
    const scans = withCost.length === 1 ? "scan" : "scans";
    return {
        text: withCost.length === runs.length
            ? `${formatUsd(total)} across ${runs.length} ${scans}`
            : `${formatUsd(total)} across ${withCost.length} of ${runs.length} scans`,
    };
}
